import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { signupForEmailOffers } from "../../api/user";
import { config } from "../../config";
import { FormInput } from "../../forms";
import { CaptchaLibrary } from "../../forms/Captcha";
import { Form } from "../../forms/Form";
import { FormSubmit } from "../../forms/FormSubmit";

import styles from "./index.module.scss";

interface IEmailModal {
    title: string;
    content: string;
    confirm_content: string;
    image: string;
    footnotes?: string;
}

export interface IEmailModalSnippet {
    email_snippet: IEmailModal;
}

const buildFootnotes = (
    footnotes: string | undefined,
    formSubmitted: boolean,
): React.ReactNode => {
    const footnotesStyles = classNames({
        [styles.footnotes]: true,
        [styles.footnotesSubmitted]: formSubmitted,
    });

    if (!footnotes) {
        return;
    }

    return (
        <div
            className={footnotesStyles}
            dangerouslySetInnerHTML={{
                __html: footnotes,
            }}
        ></div>
    );
};

export const EmailCaptureContent = (
    props: Immutable<{
        content: IEmailModalSnippet;
    }> & {
        onSubmit?: (height?: number) => void;
    },
): React.ReactNode => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [formSubmitted, setFormSubmitted] = React.useState(false);
    const [email, setEmail] = React.useState("");
    const contentElemRef = React.useRef<HTMLElement | null>(null);
    const onSubmitRef = React.useRef(props.onSubmit);
    onSubmitRef.current = props.onSubmit;

    React.useEffect(() => {
        if (!formSubmitted) {
            return;
        }

        const onSubmit = onSubmitRef.current;
        if (onSubmit && contentElemRef.current) {
            onSubmit(contentElemRef.current.getBoundingClientRect().height);
        }
    }, [formSubmitted]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        try {
            await signupForEmailOffers(email);

            setIsSubmitting(false);
            setFormSubmitted(true);
        } catch {
            setIsSubmitting(false);
        }
    };

    const handleEmailChange = async (
        event: React.FormEvent<HTMLInputElement>,
    ) => {
        const newEmail = event.currentTarget.value;
        setEmail(newEmail);
    };

    return (
        <>
            {config.get("ENABLE_EMAIL_SIGNUP_CAPTCHA") && <CaptchaLibrary />}
            <section className={styles.copySection} ref={contentElemRef}>
                {!formSubmitted && (
                    <div
                        className={`${styles.copy} rich-text`}
                        dangerouslySetInnerHTML={{
                            __html: props.content.email_snippet.content,
                        }}
                    />
                )}
                <div
                    role="status"
                    className={`${styles.copy} rich-text`}
                    dangerouslySetInnerHTML={{
                        __html: formSubmitted
                            ? props.content.email_snippet.confirm_content
                            : "",
                    }}
                />
                {!formSubmitted && (
                    <div className={styles.form}>
                        <Form onSubmit={handleSubmit}>
                            <FormInput
                                id="email_modal_email"
                                name="email_modal_email"
                                type="email"
                                label={t`Email address`}
                                labelPlacement="dynamic"
                                required={true}
                                autoComplete="email"
                                value={email}
                                onChange={handleEmailChange}
                                disabled={isSubmitting}
                            />
                            <FormSubmit type="submit" value={t`Submit`} />
                        </Form>
                    </div>
                )}
                {buildFootnotes(
                    props.content.email_snippet.footnotes,
                    formSubmitted,
                )}
            </section>
        </>
    );
};
